/**
 * Phase-reactive theming.
 *
 * The tokens themselves live in src/styles/phases.css (generated from
 * data/theme/phases.yaml): @property-registered custom properties under
 * html[data-phase="…"] blocks, transitioning over 600ms. All this hook does is
 * decide WHICH phase is active and write it — rAF-batched — to
 * documentElement.dataset.phase and <meta name="theme-color">.
 *
 * themeSource:
 *   "scroll"   one IntersectionObserver over the [data-phase] sections with a
 *              band across the viewport center (rootMargin -45%/-45%); the
 *              earliest intersecting section wins; 120ms hysteresis kills
 *              boundary flicker.
 *   "progress" the phase of your first unwatched entry — the page greets you
 *              where you actually are in the marathon.
 *   "off"      no data-phase; the road stays on the base (phase-1) tokens.
 */
import { type RefObject, useEffect, useRef } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { firstUnwatchedPhaseId } from "@/lib/theme/phase.ts";
import { useProgressStore } from "@/state/progress.ts";
import { useSettingsStore } from "@/state/settings.ts";

const HYSTERESIS_MS = 120;
const CENTER_BAND = "-45% 0px -45% 0px";

/**
 * @param observeKey any value that changes when the set of rendered
 * `[data-phase]` sections changes (filtering adds and removes them). The
 * observer re-registers on a new identity; pass nothing for a static road.
 */
export function usePhaseTheme(roadRef: RefObject<HTMLElement | null>, observeKey?: unknown): void {
  const catalog = useCatalog();
  const themeSource = useSettingsStore((s) => s.themeSource);
  const progressPhase = useProgressStore((s) =>
    themeSource === "progress" ? firstUnwatchedPhaseId(catalog, s.watched) : null,
  );

  const committedRef = useRef<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: observeKey is deliberately an unused dependency — it stands for the [data-phase] elements this effect queries out of the DOM, whose comings and goings the linter can't see.
  useEffect(() => {
    const html = document.documentElement;
    const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const themeColorFor = (phaseId: string | null): string | null => {
      const theme = catalog.themes[phaseId ?? catalog.phases[0]?.id ?? ""];
      if (!theme) return null;
      return darkQuery.matches ? theme.dark.surface : theme.light.surface;
    };

    /** rAF-batched DOM write: only ever one mutation per frame. */
    const apply = (phaseId: string | null) => {
      committedRef.current = phaseId;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (phaseId === null) delete html.dataset.phase;
        else html.dataset.phase = phaseId;
        const color = themeColorFor(phaseId);
        const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
        if (meta && color) meta.content = color;
      });
    };

    /** Candidate from the observer; committed only after it survives the hysteresis window. */
    const propose = (phaseId: string) => {
      if (phaseId === committedRef.current) {
        pendingRef.current = null;
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        return;
      }
      if (phaseId === pendingRef.current) return;
      pendingRef.current = phaseId;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (pendingRef.current !== null) apply(pendingRef.current);
        pendingRef.current = null;
      }, HYSTERESIS_MS);
    };

    // The meta color depends on the color scheme; re-apply when it flips.
    const onSchemeChange = () => apply(committedRef.current);
    darkQuery.addEventListener("change", onSchemeChange);

    let observer: IntersectionObserver | null = null;

    if (themeSource === "off") {
      apply(null);
    } else if (themeSource === "progress") {
      apply(progressPhase);
    } else {
      // One observer for the whole road. The band across the viewport center
      // decides which sections are "present"; earliest in road order wins.
      const sections = Array.from(
        roadRef.current?.querySelectorAll<HTMLElement>("[data-phase]") ?? [],
      );
      const intersecting = new Set<Element>();
      observer = new IntersectionObserver(
        (records) => {
          for (const record of records) {
            if (record.isIntersecting) intersecting.add(record.target);
            else intersecting.delete(record.target);
          }
          const winner = sections.find((el) => intersecting.has(el));
          // Nothing in the band (fast scroll, page top): keep the current phase.
          const phaseId =
            winner?.dataset.phase ?? committedRef.current ?? sections[0]?.dataset.phase;
          if (phaseId) propose(phaseId);
        },
        { rootMargin: CENTER_BAND },
      );
      for (const el of sections) observer.observe(el);
    }

    return () => {
      observer?.disconnect();
      darkQuery.removeEventListener("change", onSchemeChange);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      timerRef.current = null;
      pendingRef.current = null;
    };
  }, [catalog, themeSource, progressPhase, roadRef, observeKey]);
}
